import React, { useContext, useState } from "react";
import {
  Badge,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import { useTextDomainContext } from "@/app/providers";
import { useNavigate } from "react-router";
import { IoNotifications } from "react-icons/io5";
import { useNotificationsPreview } from "@/hooks";
import { formatTimeAgo } from "@/utils";

type NotificationPreviewItem = {
  id?: string | number;
  email?: string;
  Subject?: string;
  Message?: string;
  Read?: boolean;
  date?: string | number | Date;
};

/**
 * Top menu notification bell dropdown component.
 */
export const Notification = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const navigate = useNavigate();

  const { count, previewNotifications } = useNotificationsPreview(true);

  const [notification, setNotification] = useState<null | HTMLElement>(null);

  const openNotification = Boolean(notification);

  const handleOpenNotification = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setNotification(event.currentTarget);
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const handleItemClick = (notify: NotificationPreviewItem) => {
    handleCloseNotification();
    if (notify?.id) {
      navigate(`/user-account/notifications/${notify.id}`, {
        state: {
          notification: notify,
        },
      });
    }
  };

  return (
    <>
      <Tooltip title={gettext("Notifications")} arrow>
        <IconButton
          sx={{
            ml: 2,
          }}
          onClick={handleOpenNotification}
        >
          <Badge badgeContent={count} max={999} color="error">
            <IoNotifications size={24} color="#27272A" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Box>
        <Menu
          anchorEl={notification}
          id="account-menu"
          open={openNotification}
          onClose={handleCloseNotification}
          transformOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
          anchorOrigin={{
            horizontal: "right",
            vertical: "bottom",
          }}
        >
          {(previewNotifications?.length ?? 0) === 0 ? (
            <MenuItem
              disabled
              sx={{
                minWidth: 260,
                py: 2,
                px: 2,
                justifyContent: "center",
                cursor: "default",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", textAlign: "center" }}
              >
                {gettext("No unread notifications")}
              </Typography>
            </MenuItem>
          ) : (
            previewNotifications?.map((notify: NotificationPreviewItem) => {
              return (
                <Box key={notify?.id}>
                  <MenuItem
                    sx={{
                      color: "primary.main",
                      alignItems: "flex-start",
                      py: 1.5,
                      px: 2,
                      maxWidth: 360,
                    }}
                    onClick={() => handleItemClick(notify)}
                  >
                    <ListItemIcon
                      sx={{
                        color: "text.secondary",
                        minWidth: 36,
                        mt: 0.25,
                      }}
                    >
                      <EmailIcon fontSize="small" />
                    </ListItemIcon>
                    <Box
                      component="div"
                      sx={{ whiteSpace: "normal", flex: 1, minWidth: 0 }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "text.primary",
                          lineHeight: 1.4,
                          mb: notify?.date ? 0.5 : 0,
                          wordBreak: "break-word",
                        }}
                      >
                        {notify?.Subject || gettext("No Subject")}
                      </Typography>
                      {notify?.date && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: 12,
                            display: "block",
                          }}
                        >
                          {formatTimeAgo(notify?.date)}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                  <Divider />
                </Box>
              );
            })
          )}
        </Menu>
      </Box>
    </>
  );
};
